import notification from "antd/es/notification";
import { push } from "connected-react-router";
import { addMinutes, isBefore } from "date-fns";
import { uniqBy } from "lodash-es";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { OrganizationSetting } from "@mapmycustomers/shared";
import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  EntityType,
  EntityTypesSupportedByIntegrations,
} from "@mapmycustomers/shared/types/entity";
import IntegrationRaw from "@mapmycustomers/shared/types/integrations/IntegrationRaw";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isApiError, isDefined } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import StatusOption from "@app/scene/settings/component/Integrations/enum/StatusOption";
import { PAGE_LIMIT } from "@app/scene/settings/component/Integrations/utils/consts";
import integrationsSettings from "@app/scene/settings/component/Integrations/utils/integrationsSettings";
import { withReauthState } from "@app/scene/settings/component/Integrations/utils/reauthState";
import { callApi } from "@app/store/api/callApi";
import { fetchCustomFields } from "@app/store/customFields/actions";
import { handleError } from "@app/store/errors/actions";
import { getOrganization, getOrganizationId, getOrganizationSettingValue } from "@app/store/iam";
import CreateIntegrationPayload from "@app/types/integrations/CreateIntegrationPayload";
import Integration from "@app/types/integrations/Integration";
import IntegrationField from "@app/types/integrations/IntegrationField";
import IntegrationFieldResponse from "@app/types/integrations/IntegrationFieldResponse";
import IntegrationSync from "@app/types/integrations/IntegrationSync";
import IntegrationUser from "@app/types/integrations/IntegrationUser";
import PlatformIntegrationField from "@app/types/integrations/PlatformIntegrationField";
import SalesforceAuthUrl from "@app/types/integrations/SalesforceAuthUrl";
import IntegrationViewState from "@app/types/viewModel/IntegrationSyncsViewState";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import getFieldNameByIntegrationName from "@app/util/fieldModel/getFieldNameByIntegrationName";
import parseIntegrationRecord from "@app/util/integrations/parseIntegrationRecord";
import { findServiceByType } from "@app/util/integrations/useServiceDescription";

import integrationFieldModel from "../../util/fieldModel/IntegrationFieldModel";

import {
  changeOrgUrl,
  createIntegration,
  deleteIntegration,
  fetchIntegration,
  generatePemFile,
  initializeEditPage,
  initializeHomePage,
  initializeMappingStep,
  reauthenticateIntegration,
  saveIntegration,
  selectService,
  setCurrentIntegration,
  setCustomFieldIntegrationResponse,
  updateIntegrationMetadata,
  updateIntegrationSyncsViewState,
} from "./actions";
import getSchedule from "./getSchedule";
import {
  CreateNewCustomField,
  EmptyIntegrationFieldsMappingState,
  IntegrationFieldsMapping,
} from "./index";
import {
  getCurrentIntegration,
  getIntegrationFields,
  getIntegrationUsers,
  getIntegrationViewState,
} from "./selectors";

const messages = defineMessages({
  reauthenticateError: {
    id: "integrations.reauthenticate.error",
    defaultMessage:
      "We couldn't re-authenticate this integration. Please try again or contact support.",
    description: "Generic error when integration re-authentication fails",
  },
  reauthenticateMissingCode: {
    id: "integrations.reauthenticate.missingCode",
    defaultMessage: "Re-authentication didn't complete. Please try again.",
    description: "Error shown when the OAuth code is missing during re-authentication (v0368)",
  },
  reauthenticateSuccess: {
    id: "integrations.reauthenticate.success",
    defaultMessage: "Integration re-authenticated successfully.",
    description: "Toast shown when an integration is re-authenticated successfully",
  },
  reauthenticateTenantMismatch: {
    id: "integrations.reauthenticate.tenantMismatch",
    defaultMessage:
      "Re-authentication is for a different account than the existing integration. Please authenticate with the same account.",
    description: "Error shown when re-authenticating with a different account/tenant (v1062)",
  },
});

const redirectTo = (url: string) => {
  window.location.href = url;
};

const processMmcFieldName = (integrationField: IntegrationField): IntegrationField => {
  if (!integrationField.mmcField) {
    return integrationField;
  }

  const fieldModel = getFieldModelByEntityType(integrationField.entityType);
  const fieldName = getFieldNameByIntegrationName(fieldModel, integrationField.mmcField);
  return fieldName ? { ...integrationField, mmcField: fieldName } : integrationField;
};

const convertMmcFieldToSave = (
  entityType: EntityTypesSupportedByIntegrations,
  mmcField: null | string
): null | string => {
  if (!mmcField) {
    return mmcField;
  }
  const fieldModel = getFieldModelByEntityType(entityType);
  const field = fieldModel.getByName(mmcField);
  if (field) {
    return field.integrationName;
  }
  return mmcField;
};

export function* onSelectService(action: ReturnType<typeof selectService>) {
  try {
    const { integrationService, isSandbox, reauthIntegrationId } = action.payload;
    if (!isDefined(reauthIntegrationId)) {
      // A normal "add integration" flow must clear any id left behind by an abandoned
      // re-authentication, otherwise its OAuth callback would re-authenticate that old
      // integration instead of creating a new one.
      integrationsSettings.resetReauthIntegrationId();
    }
    let url: string | undefined;
    if (integrationService === IntegrationService.SALESFORCE) {
      // Salesforce requires PKCE, whose code_challenge is per-attempt and so cannot live in a
      // build-time static URL. The server mints the URL and holds the matching verifier against
      // the `state` embedded in it. No fallback to the static URL on failure — that would
      // silently downgrade the exchange to non-PKCE.
      const orgId: Organization["id"] = yield select(getOrganizationId);
      // The response also carries `state`, but only as a convenience for callers that want to
      // inspect it — it is already a query param on `url`, and Salesforce echoes it back on the
      // callback, which is how it reaches the create/reauth payload. Redirecting to `url`
      // unmodified is what makes the round trip work.
      const { url: salesforceAuthUrl }: SalesforceAuthUrl = yield callApi(
        "fetchSalesforceAuthUrl",
        orgId,
        isSandbox ? "sandbox" : "production"
      );
      url = salesforceAuthUrl;
    } else {
      const service = findServiceByType(integrationService);
      url = isSandbox ? service?.sandboxUrl : service?.url;
    }
    if (url) {
      if (isDefined(reauthIntegrationId)) {
        // The `state` param survives a callback that lands on a different origin than this page;
        // sessionStorage is the same-origin fallback.
        integrationsSettings.setReauthIntegrationId(reauthIntegrationId);
        yield call(redirectTo, withReauthState(url, reauthIntegrationId));
        return;
      }
      yield call(redirectTo, url);
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onCreateIntegration({
  payload: { details, failureCallback, successCallback },
}: ReturnType<typeof createIntegration.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    let serviceName = details.get("serviceName");
    const state = details.get("state");
    if (state === "dynamics-production") {
      serviceName = IntegrationService.DYNAMICS;
    }
    const code = details.get("code");
    const envType = details.get("envType");
    const data: CreateIntegrationPayload = {
      code,
      envType: serviceName === IntegrationService.DYNAMICS ? "production" : envType,
      type: serviceName as IntegrationService,
    };
    if (serviceName === IntegrationService.ZOHO) {
      data.accountsUrl = details.get("accounts-server");
    }

    if (serviceName === IntegrationService.SALESFORCE) {
      // AddIntegration has already stripped our reauth marker, so this is Salesforce's own state:
      // the opaque token the server minted alongside the authorize URL, which keys the PKCE
      // verifier it needs to complete the exchange.
      data.state = state;
    }

    if (serviceName === IntegrationService.NETSUITE) {
      data.accountId = details.get("accountId");
      data.clientId = details.get("clientId");
      data.certId = details.get("certId");
    }

    if (!code && serviceName !== IntegrationService.NETSUITE) {
      throw new Error(`Invalid ${serviceName} authorization, please try again or contact support`);
    }
    const response: Integration = yield callApi("createIntegration", org.id, data);
    yield put(createIntegration.success(response));
    if (successCallback) {
      yield call(successCallback, response.id);
    }

    if (serviceName !== IntegrationService.NETSUITE) {
      yield put(
        push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}/edit/${response.id}`)
      );
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(createIntegration.failure(error));

    if (failureCallback) {
      yield call(failureCallback);
    }

    yield put(push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}`));
  }
}

function* onInitializeHomePage() {
  try {
    const org: Organization = yield select(getOrganization);
    const response: ListResponse<IntegrationRaw> = yield callApi("fetchIntegrations", org.id);
    const integrations: Integration[] = response.data.map(parseIntegrationRecord);
    if (integrations.length) {
      const integration: Integration = integrations[0];
      const integrationSyncsResponse: ListResponse<IntegrationSync> = yield callApi(
        "fetchIntegrationSyncs",
        org.id,
        integration.id,
        {
          $limit: 1,
          $order: "-createdAt",
        }
      );
      yield put(
        initializeHomePage.success({
          integration,
          lastIntegrationSync:
            integrationSyncsResponse.data.length > 0 ? integrationSyncsResponse.data[0] : undefined,
        })
      );
      const viewState: IntegrationViewState = yield select(getIntegrationViewState);
      yield put(updateIntegrationSyncsViewState.request({ viewState }));
    } else {
      yield put(initializeHomePage.success({ integration: undefined }));
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(initializeHomePage.failure(error));
  }
}

function* onUpdateIntegrationViewState({
  payload: { page },
}: ReturnType<typeof updateIntegrationSyncsViewState.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: IntegrationViewState = yield select(getIntegrationViewState);
    const integration: Integration = yield select(getCurrentIntegration);
    const filters =
      viewState.statusOption === StatusOption.COMPLETED_WITH_ERRORS
        ? {
            $and: [
              {
                $or: [
                  {
                    "errored.incoming": {
                      $gt: 0,
                    },
                  },
                  {
                    "errored.outgoing": {
                      $gt: 0,
                    },
                  },
                ],
              },
              {
                status: "errored",
              },
              {
                createdAt: {
                  $gte: viewState.dateRange?.startDate.toISOString(),
                  $lte: viewState.dateRange?.endDate.toISOString(),
                },
              },
            ],
          }
        : {
            $and: [
              {
                status: viewState.statusOption,
              },
              {
                createdAt: {
                  $gte: viewState.dateRange?.startDate.toISOString(),
                  $lte: viewState.dateRange?.endDate.toISOString(),
                },
              },
            ],
          };

    if (integration) {
      let order = "-createdAt";
      if (viewState.sort) {
        const field = integrationFieldModel.getByName(viewState.sort);
        if (field) {
          order = field.platformFilterNames[0];
          if (viewState.sortOrder === SortOrder.DESC) {
            order = `-${order}`;
          }
        }
      }
      const integrationSyncsResponse: ListResponse<IntegrationSync> = yield callApi(
        "fetchIntegrationSyncs",
        orgId,
        integration.id,
        {
          $filters: filters,
          $limit: PAGE_LIMIT,
          $offset: ((page ?? 1) - 1) * PAGE_LIMIT,
          $order: order,
        }
      );
      yield put(
        updateIntegrationSyncsViewState.success({
          integrationSyncs: integrationSyncsResponse.data,
          total: integrationSyncsResponse.total,
        })
      );
    } else {
      yield put(updateIntegrationSyncsViewState.success({ integrationSyncs: [], total: 0 }));
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(updateIntegrationSyncsViewState.failure(error));
  }
}

function* onInitializeEditPage(action: ReturnType<typeof initializeEditPage.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const orgTimeZone: string = yield select(
      getOrganizationSettingValue,
      OrganizationSetting.TIMEZONE
    );
    const integrationId = action.payload;
    const rawIntegration: IntegrationRaw = yield callApi("fetchIntegration", org.id, integrationId);
    const integration = parseIntegrationRecord(rawIntegration);
    const defaultSchedule = getSchedule(orgTimeZone);
    if (
      (integration?.metadata?.instances ?? []).length > 1 &&
      !integration?.metadata?.instanceSelected
    ) {
      yield put(
        initializeEditPage.success({
          defaultSchedule: defaultSchedule,
          integration: { ...integration, schedule: integration?.schedule ?? defaultSchedule },
          needToSelectDynamicsInstance: true,
        })
      );
      return;
    }

    const [integrationUsersResponse, integrationSyncsResponse]: [
      ListResponse<IntegrationUser>,
      ListResponse<IntegrationSync>,
    ] = yield all([
      callApi("fetchIntegrationUsers", org.id, integrationId, {
        $filters: {
          $and: [{ active: true }],
        },
        $limit: 10000,
        $order: ["firstName", "lastName"],
      }),
      callApi("fetchIntegrationSyncs", org.id, integrationId, {
        $limit: 1000,
        $order: "-createdAt",
      }),
    ]);

    // note syncing should be enabled by default
    if (!integration.metadata?.noteSyncing) {
      integration.metadata = {
        ...(integration.metadata ?? {}),
        noteSyncing: {
          [EntityType.ACTIVITY]: false,
          [EntityType.COMPANY]: true,
          [EntityType.DEAL]: true,
          [EntityType.PERSON]: true,
        },
      };
    }

    if (!integration.metadata?.fileSyncing) {
      integration.metadata = {
        ...(integration.metadata ?? {}),
        fileSyncing: {
          [EntityType.ACTIVITY]: false,
          [EntityType.COMPANY]: true,
          [EntityType.DEAL]: true,
          [EntityType.PERSON]: true,
        },
      };
    }

    yield put(
      initializeEditPage.success({
        defaultSchedule,
        integration: { ...integration, schedule: integration?.schedule ?? defaultSchedule },
        integrationFields: { ...EmptyIntegrationFieldsMappingState },
        integrationSyncs: integrationSyncsResponse.data,
        integrationUsers: integrationUsersResponse.data,
      })
    );
  } catch (error) {
    yield put(handleError({ error }));
    yield put(initializeEditPage.failure(error));
  }
}

export function* onInitializeMappingStep() {
  try {
    const org: Organization = yield select(getOrganization);
    const integration: Integration = yield select(getCurrentIntegration);
    const syncedEntityTypes = (
      Object.keys(integration.syncOptions) as EntityTypesSupportedByIntegrations[]
    ).filter(
      (entityType) =>
        integration.syncOptions[entityType].incoming || integration.syncOptions[entityType].outgoing
    );

    const responses: ListResponse<IntegrationField>[] = yield all(
      syncedEntityTypes.map((entityType) =>
        callApi("fetchIntegrationFields", org.id, integration.id, {
          $filters: { entityType },
          $limit: 2000,
          $order: "crmDisplayName",
        })
      )
    );

    const currentIntegrationFields: IntegrationFieldsMapping = yield select(getIntegrationFields);
    yield put(
      initializeMappingStep.success({
        integrationFields: {
          ...EmptyIntegrationFieldsMappingState,
          ...syncedEntityTypes.reduce<Partial<IntegrationFieldsMapping>>(
            (result, entityType, i) => ({
              ...result,
              [entityType]: uniqBy(
                [
                  ...currentIntegrationFields[entityType], // current mapping overrides loaded
                  ...responses[i].data.map(processMmcFieldName),
                ],
                "id"
              ),
            }),
            {}
          ),
        },
      })
    );
  } catch (error) {
    yield put(initializeMappingStep.failure(error));
    yield put(handleError({ error }));
  }
}

function* onFetchIntegration(action: ReturnType<typeof fetchIntegration.request>) {
  const org: Organization = yield select(getOrganization);
  try {
    const response: IntegrationRaw = yield callApi("fetchIntegration", org.id, action.payload);
    const integration = parseIntegrationRecord(response);
    yield put(fetchIntegration.success(integration));
    yield put(setCurrentIntegration({ currentIntegration: integration }));
  } catch (error) {
    yield put(fetchIntegration.failure(error));
    yield put(handleError({ error }));
  }
}

function* onDeleteIntegration(action: ReturnType<typeof deleteIntegration.request>) {
  const org: Organization = yield select(getOrganization);
  try {
    yield callApi("deleteIntegration", org.id, action.payload);
    yield put(deleteIntegration.success());
    yield put(initializeHomePage.request());
  } catch (error) {
    yield put(deleteIntegration.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onReauthenticateIntegration({
  payload: { details, integrationId },
}: ReturnType<typeof reauthenticateIntegration.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // Zoho posts its region host as `accounts-server`; the backend expects it as accountsUrl.
    const serviceName = details.get("serviceName");
    const payload =
      serviceName === IntegrationService.NETSUITE
        ? {
            accountId: details.get("accountId"),
            certId: details.get("certId"),
            clientId: details.get("clientId"),
          }
        : {
            accountsUrl: details.get("accounts-server"),
            code: details.get("code"),
            // Re-authentication runs the same server-side authorization-code exchange as create,
            // so Salesforce's PKCE state has to travel with it. No other provider sends one.
            ...(serviceName === IntegrationService.SALESFORCE
              ? { state: details.get("state") }
              : {}),
          };
    yield callApi("reauthenticateIntegration", orgId, integrationId, payload);
    yield put(reauthenticateIntegration.success());
    notification.success({
      message: i18nService.formatMessage(messages.reauthenticateSuccess),
    });
    // Reached from the /add OAuth callback (AddIntegration) and, for NetSuite, from the wizard on
    // the details page — either way a real navigation, so the list remounts and re-fetches.
    yield put(push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}`));
  } catch (error) {
    yield put(reauthenticateIntegration.failure(error));
    yield put(
      handleError({
        defaultMessage: messages.reauthenticateError,
        error,
        messageByErrorCodeMap: {
          v0368: messages.reauthenticateMissingCode,
          v1062: messages.reauthenticateTenantMismatch,
        },
      })
    );
    yield put(push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}`));
  }
}

function* onSaveIntegration() {
  try {
    const integration: Integration | undefined = yield select(getCurrentIntegration);
    if (!integration || !integration.id) {
      return;
    }
    const org: Organization = yield select(getOrganization);
    const schedule = { ...integration.schedule };
    const oneMinuteInFuture: Date = addMinutes(Date.now(), 1);
    if (!schedule.nextRunAt || isBefore(schedule.nextRunAt, oneMinuteInFuture)) {
      schedule.nextRunAt = oneMinuteInFuture;
    }
    const payload = {
      id: integration.id,
      isLocked: integration.isLocked,
      metadata: integration.metadata,
      schedule,
      syncOptions: integration.syncOptions,
    };
    yield callApi("updateIntegration", org.id, integration.id, payload);

    const integrationUsers: IntegrationUser[] = yield select(getIntegrationUsers);
    yield callApi(
      "updateIntegrationUsers",
      org.id,
      integration.id,
      integrationUsers.map(({ id, syncing, userId }) => ({ id, syncing, userId }))
    );

    const integrationFields: IntegrationFieldsMapping = yield select(getIntegrationFields);

    const updateIntegrationFieldsPayload: Partial<
      Record<EntityTypesSupportedByIntegrations, PlatformIntegrationField[]>
    > = {};

    (Object.keys(integrationFields) as EntityTypesSupportedByIntegrations[])
      .filter(
        (entityType) =>
          integration.syncOptions[entityType] &&
          (integration.syncOptions[entityType].incoming ||
            integration.syncOptions[entityType].outgoing)
      )
      .forEach((entityType) => {
        updateIntegrationFieldsPayload[entityType] = integrationFields[entityType].map(
          ({ id, customField, mmcField, mmcGroupField }) => ({
            id,
            customField: !!customField,
            mmcField:
              mmcField === CreateNewCustomField
                ? null
                : convertMmcFieldToSave(entityType, mmcField),
            mmcGroupField,
            syncing: mmcField === CreateNewCustomField || !!mmcField,
          })
        );
      });

    const integrationFieldsResponses: Partial<
      Record<EntityTypesSupportedByIntegrations, IntegrationFieldResponse[]>
    > = yield callApi(
      "updateIntegrationFields",
      org.id,
      integration.id,
      updateIntegrationFieldsPayload
    );
    const customFieldRes: IntegrationFieldResponse[] =
      Object.values(integrationFieldsResponses).reduce(
        (result: IntegrationFieldResponse[], response?: IntegrationFieldResponse[]) =>
          response
            ? result.concat(
                response.filter(
                  (mmcFieldItem: IntegrationFieldResponse) => mmcFieldItem.customField
                )
              )
            : result,
        []
      ) || [];

    yield all(
      (Object.keys(integrationFields) as EntityTypesSupportedByIntegrations[])
        .filter((entityType) =>
          integrationFields[entityType].some(({ customField }) => customField)
        )
        .map((entityType) => put(fetchCustomFields.request({ entityType })))
    );

    yield put(setCustomFieldIntegrationResponse(customFieldRes));
    yield put(saveIntegration.success(payload as Integration));
  } catch (error) {
    yield put(saveIntegration.failure(error));
    yield put(handleError({ error }));
  }
}

function* onChangeOrgUrl({ payload: orgUrl }: ReturnType<typeof changeOrgUrl.request>) {
  try {
    const integration: Integration | undefined = yield select(getCurrentIntegration);
    if (!integration || !integration.id) {
      return;
    }
    const org: Organization = yield select(getOrganization);
    const payload = {
      id: integration.id,
      isLocked: integration.isLocked,
      metadata: {
        ...integration.metadata,
        instanceSelected: true,
        orgUrl,
      },
      schedule: integration.schedule,
      syncOptions: integration.syncOptions,
    };
    yield callApi("updateIntegration", org.id, integration.id, payload);
    yield put(changeOrgUrl.success());
    yield put(initializeEditPage.request(integration.id));
  } catch (error) {
    yield put(changeOrgUrl.failure(error));
    yield put(handleError({ error }));
  }
}

function* onUpdateIntegrationMetadata({
  payload,
}: ReturnType<typeof updateIntegrationMetadata.request>) {
  try {
    const integration: Integration | undefined = yield select(getCurrentIntegration);
    if (!integration || !integration.id) {
      return;
    }
    const org: Organization = yield select(getOrganization);
    const payloadData = {
      id: integration.id,
      isLocked: integration.isLocked,
      metadata: {
        ...integration.metadata,
        ...payload,
      },
      syncOptions: integration.syncOptions,
    };
    const updatedIntegration: IntegrationRaw = yield callApi(
      "updateIntegration",
      org.id,
      integration.id,
      payloadData
    );
    yield put(updateIntegrationMetadata.success(updatedIntegration.metadata));
  } catch (error) {
    yield put(updateIntegrationMetadata.failure(error));
  }
}

function* onGeneratePemFile({
  payload: { callback, force },
}: ReturnType<typeof generatePemFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    let pemFile: string | undefined;

    if (force) {
      yield callApi("generateIntegrationPemFile", org.id);
    }

    try {
      pemFile = yield callApi("downloadIntegrationPemFile", org.id);
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        yield callApi("generateIntegrationPemFile", org.id);
        pemFile = yield callApi("downloadIntegrationPemFile", org.id);
      }
    }

    if (callback && pemFile) {
      yield call(callback, pemFile);
    }
    yield put(generatePemFile.success());
  } catch (error) {
    yield put(generatePemFile.failure(error));
    yield put(handleError({ error }));
  }
}

export function* integrationsSaga() {
  yield takeLatest(selectService, onSelectService);
  yield takeLatest(createIntegration.request, onCreateIntegration);
  yield takeLatest(initializeEditPage.request, onInitializeEditPage);
  yield takeLatest(initializeHomePage.request, onInitializeHomePage);
  yield takeLatest(initializeMappingStep.request, onInitializeMappingStep);
  yield takeLatest(fetchIntegration.request, onFetchIntegration);
  yield takeLatest(deleteIntegration.request, onDeleteIntegration);
  yield takeLatest(reauthenticateIntegration.request, onReauthenticateIntegration);
  yield takeLatest(saveIntegration.request, onSaveIntegration);
  yield takeLatest(changeOrgUrl.request, onChangeOrgUrl);
  yield takeEvery(updateIntegrationSyncsViewState.request, onUpdateIntegrationViewState);
  yield takeLatest(updateIntegrationMetadata.request, onUpdateIntegrationMetadata);
  yield takeLatest(generatePemFile.request, onGeneratePemFile);
}
